import { useEffect, useMemo, useState, useRef } from 'react';
import { TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  FlexContainer,
  LoadingPage,
  PaginatedTable,
  PaginatedTableProps,
} from '@act/storybook';
import { CustomizationThemeDto } from '@act/shared/data-transfer-objects';
import { useApiCall } from 'redux/utils/api-hook';
import { appSettingsRequests } from 'redux/requests/app-settings.requests';
import { AdminToolSkeleton } from '../AdminToolSkeleton';
import { AdminDrawer } from '../AdminDrawer';
import { CreateEditCustomizationTheme } from './CreateEditCustomizationTheme';
import { CustomizationThemeTableColumns } from './CustomizationThemeTableColumns';
import { IndexChildContainer } from 'react-components/member-index/indexContainer';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice';
import NoDataMessage from 'toolkit/tables/NoDataMessage';

export const CustomizationThemeView = () => {
  const actingUser = actingUserAppSelectors.useSelectActingUser();
  const hasInitialized = useRef(false);

  const [themes, themesLoading, getAllThemes] = useApiCall(
    appSettingsRequests.getAllCustomizationThemes
  );
  const [selectedTheme, setSelectedTheme] =
    useState<CustomizationThemeDto | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [createNewTheme, setCreateNewTheme] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Only fetch data once acting user is loaded
    if (!actingUser || hasInitialized.current) return;

    void getAllThemes({ params: { includeDeleted: true } });
    hasInitialized.current = true;
  }, [actingUser]);

  const handleRefresh = () => {
    void getAllThemes({ params: { includeDeleted: true } });
  };

  const setFormOpen = (value: boolean) => {
    if (!value) {
      setSelectedTheme(null);
      setEditMode(false);
    }
  };

  const columns = useMemo(() => {
    return CustomizationThemeTableColumns();
  }, []);

  const filteredData = useMemo(() => {
    if (!themes) return [];
    if (!searchTerm.trim()) {
      return themes;
    }
    return themes.filter(theme => {
      const themeData =
        `${theme.name} ${theme.description} ${theme.category}`.toLowerCase();
      return themeData.includes(searchTerm.toLowerCase());
    });
  }, [themes, searchTerm]);

  const THEME_ADMIN_TABLE_PROPS: PaginatedTableProps<CustomizationThemeDto> = {
    data: filteredData,
    columnConfig: columns,
    inMemoryPagination: true,
    onRowClick: theme => {
      setSelectedTheme(theme);
      setEditMode(true);
    },
  };

  if (!actingUser || themesLoading || !themes) {
    return <LoadingPage />;
  }

  return (
    <AdminToolSkeleton
      title="Customization Themes"
      toolDescription="Manage customization themes with logo and accent gradients for the application."
      buttonContent="Add New Theme"
      variant="contained"
      onClick={() => {
        setCreateNewTheme(true);
      }}
      startIcon={<AddIcon sx={{ ml: 1 }} />}
    >
      <TextField
        label="Search by name, description, or category"
        variant="outlined"
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        fullWidth
        margin="normal"
        sx={{ backgroundColor: '#ffffff' }}
      />
      <IndexChildContainer>
        {filteredData.length === 0 ? (
          <FlexContainer
            direction="row"
            justifyContent="center"
            alignItems="center"
            height="100%"
            width="100%"
            padding={6}
          >
            <NoDataMessage
              iconName="Palette"
              message="Uh oh! We can't find any themes!"
              subMessage="Start customizing some right away!"
            />
          </FlexContainer>
        ) : (
          <PaginatedTable {...THEME_ADMIN_TABLE_PROPS} />
        )}
      </IndexChildContainer>
      <AdminDrawer
        open={createNewTheme || editMode}
        setOpen={createNewTheme ? setCreateNewTheme : setFormOpen}
        title={
          createNewTheme
            ? 'Create Customization Theme'
            : `Edit ${selectedTheme?.name}`
        }
      >
        {(createNewTheme || editMode) && (
          <CreateEditCustomizationTheme
            theme={selectedTheme ? selectedTheme : undefined}
            createMode={createNewTheme}
            onClose={() => {
              setCreateNewTheme(false);
              setEditMode(false);
              setSelectedTheme(null);
            }}
            onSuccess={handleRefresh}
            allThemes={themes || []}
          />
        )}
      </AdminDrawer>
    </AdminToolSkeleton>
  );
};
